import DashboardProjectManagerLayout from "components/DashboardProjectManager";
import { observer } from "mobx-react";
import DataTable from "../../../components/DataTable";
import MDBox from "../../../components/MDBox";
import { dataTableModel } from "./utils";
import { useEffect, useState } from "react";
import { showMessage, useApi } from "../../../services/helpers";
import pxToRem from "../../../assets/theme/functions/pxToRem";
import MDButton from "../../../components/MDButton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ROLES, ROUTES } from "../../../services/constants";
import SearchBar from "../../../components/SearchBar";
import { useStores } from "../../../models";
import OneSignal from "react-onesignal";
import shop_button from "./../../../assets/images/button/shop_button.JPG";
import stripe from "../../../assets/images/stripe.png";
import MDTypography from "components/MDTypography";
import { Modal, Switch } from "@mui/material";

function ProjectsPage({ selectedButtonId }) {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rootStore = useStores();
  const { loginStore } = rootStore;
  const [loading, setLoading] = useState(false);
  const [datatable, setDatatable] = useState({ ...dataTableModel });
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfItems, setNumberOfItems] = useState(0);
  const [numberOfItemsPage, setNumberOfItemsPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [MyProfile, setMyProfile] = useState(null);

  const getStripeSettings = async () => {
    await api
      .getStripeSettings()
      .then((response) => {
        if (response.kind === "ok") {
          console.log("MyProfile__1", response.data);
          console.log("MyProfile", response.data?.reminder_dismissed);
          setMyProfile(response.data);
        }
      })
      .catch(() => {
        showMessage("Error getting profile");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getStripeSettings();
  }, []);

  const getAllProjects = (searchData = "", page = 1, ordering = "") => {
    setLoading(true);
    api
      .getProjects({ search: searchData, page, ordering })
      .then((result) => {
        if (result.kind === "ok") {
          const { count, results } = result.data;
          const tmp = { ...dataTableModel };
          tmp.rows = results.map((e) => renderTableRow(e));
          setDatatable(tmp);
          setNumberOfItems(count);
          setNumberOfItemsPage(results.length);
        }
      })
      .catch((err) => showMessage())
      .finally(() => setLoading(false));
  };

  const getShoppingCartItems = (projectId) => {
    setLoading(true);
    api
      .getShoppingCartQuantity(projectId)
      .then((result) => {
        if (result.kind === "ok") {
          const { quantity } = result.data;
          loginStore.setShoppingCartQuantity(quantity);
        }
      })
      .catch((err) => showMessage())
      .finally(() => setLoading(false));
  };

  const handleGoToProject = (project) => {
    if (project?.id) {
      getShoppingCartItems(project.id);
      if (loginStore.group === ROLES.SUPPLY_MANAGER.name) {
        navigate(ROUTES.USER_SHOPPING_CART(project?.id));
      } else {
        navigate(ROUTES.USER_PROJECT_SELECTED(project?.id));
      }
    } else if (project === null) {
      showMessage("Please select a project", "warning");
    }
  };

  const handleStripeOnboarding = async () => {
    setOpenStripeModal(false);
    try {
      const result = await api.manageConnectAccount();
      if (result.kind === "ok") {
        if (result.data?.onboarding_url) {
          window.location.href = result.data.onboarding_url;
        } else {
          showMessage("Stripe account already connected", "success");
        }
      } else {
        showMessage("Failed to start onboarding", "error");
      }
    } catch (e) {
      showMessage("Failed to start onboarding", "error");
    }
  };

  const handleMaybeLater = async () => {
    try {
      await api.updateStripeSettings({ reminder_dismissed: true });
    } catch (e) {
      // silently ignore — reminder_dismissed is best-effort
    } finally {
      setOpenStripeModal(false);
    }
  };

  const renderTableRow = (item) => {
    item.style = [{ backgroundColor: "#BEF183" }];
    item.company__name = item.company.name;
    item.region__name = item.region.name;
    item.brand__name = item.brand.name;
    return item;
  };

  const initOneSignal = async () => {
    try {
      await OneSignal.init({ appId: "dd3adfa1-205f-4f12-b994-748077112a0a" });
      OneSignal.showNativePrompt();
      const pushNotification = await OneSignal.isPushNotificationsEnabled();
      const userId = await OneSignal.getUserId();
      if (userId) {
        const { data } = await api.setDevice({ user_id: userId, push_token: "", active: true });
      }
      const subscription = await OneSignal.getSubscription();
      const notificationPermission = await OneSignal.getNotificationPermission();
      const isPushNotificationsEnabled = await OneSignal.isPushNotificationsEnabled();
      OneSignal.on("subscriptionChange", () => {});
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    initOneSignal();
  }, []);

  useEffect(() => {
    const checkStripeStatus = async () => {
      if (loginStore.group !== ROLES.SUPPLY_MANAGER.name) return;
      if (!MyProfile) return; // wait until profile is loaded
      if (MyProfile.reminder_dismissed) return;

      try {
        const result = await api.getStripeAccountStatus();

        if (result.kind === "ok") {
          const { has_account, onboarding_status } = result.data;
          if (!has_account || onboarding_status !== "verified") {
            setOpenStripeModal(true);
          }
        } else {
          // API error — show popup so the SM is always prompted to complete onboarding
          setOpenStripeModal(true);
        }
      } catch (e) {
        // Network/unexpected error — still show the popup for SMs
        setOpenStripeModal(true);
      }
    };

    checkStripeStatus();
  }, [loginStore.group, MyProfile]);

  useEffect(() => {
    if (loginStore.group !== ROLES.SUPPLY_MANAGER.name) return;

    if (searchParams.get("success") === "true") {
      // SM returned from Stripe after completing onboarding
      showMessage("Your Stripe account has been successfully verified!", "success");
      navigate(ROUTES.USER_PROJECT_SELECTOR, { replace: true });
    } else if (searchParams.get("refresh") === "true") {
      // Onboarding link expired — get a fresh one
      handleStripeOnboarding();
    }
  }, []);

  if (searchParams.get("refresh") === "true" && loginStore.group === ROLES.SUPPLY_MANAGER.name) {
    return null;
  }

  return (
    <DashboardProjectManagerLayout
      justifyContent={"space-between"}
      title={"Select Project"}
      selectedButtonId={selectedButtonId}
      header={
        <SearchBar loading={loading} search={getAllProjects} setSearchQuery={setSearchQuery} />
      }
    >
      <MDBox sx={{ overflow: "visible", width: "100%" }}>
        <DataTable
          loading={loading}
          loadingText={"Loading..."}
          table={datatable}
          currentPage={currentPage}
          numberOfItems={numberOfItems}
          numberOfItemsPage={numberOfItemsPage}
          setSelectedProject={setSelectedProject}
          selectedProject={selectedProject}
          searchFunc={getAllProjects}
          searchQuery={searchQuery}
          onPageChange={(page) => {
            getAllProjects(searchQuery, page);
            setCurrentPage(page);
          }}
        />
      </MDBox>
      <MDBox width={"100%"} display={"flex"} mt={3}>
        <MDBox width={"100%"} display={"flex"} justifyContent={"flex-end"}>
          <MDButton
            color={"secondary"}
            sx={{ width: pxToRem(280) }}
            onClick={() => handleGoToProject(selectedProject)}
          >
            Go To Project
          </MDButton>
        </MDBox>
      </MDBox>
      {loginStore.is_alliance_employee && (
        <MDBox
          component={"img"}
          src={shop_button}
          alt={"shop"}
          width={pxToRem(400)}
          height={pxToRem(200)}
          borderRadius={"5%"}
          sx={{ border: "1px solid", cursor: "pointer" }}
          onClick={() => window.open(loginStore.shopify_link, "_blank")}
        ></MDBox>
      )}

      {/* Stripe Onboarding Modal */}
      <Modal open={openStripeModal} onClose={() => {}} disableEscapeKeyDown>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: pxToRem(500),
            bgcolor: "#ffffff",
            borderRadius: pxToRem(20),
            p: `${pxToRem(40)} ${pxToRem(36)}`,
            outline: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: pxToRem(16),
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          }}
        >
          {/* Stripe logo — proper circle container */}
          <MDBox
            sx={{
              width: pxToRem(72),
              height: pxToRem(72),
              borderRadius: "50%",
              bgcolor: "#6772e5",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: pxToRem(4),
            }}
          >
            <MDBox
              component="img"
              src={stripe}
              alt="Stripe"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </MDBox>

          <MDTypography variant="h5" fontWeight="bold" textAlign="center" sx={{ color: "#1a1a2e" }}>
            Stripe account verification required
          </MDTypography>

          <MDTypography variant="body2" textAlign="center" sx={{ color: "#555", lineHeight: 1.7 }}>
            Your Stripe onboarding process is not yet completed.
          </MDTypography>

          <MDTypography
            variant="body2"
            textAlign="center"
            sx={{ color: "#777", lineHeight: 1.7, px: pxToRem(8) }}
          >
            To start receiving payments and process transactions, you must complete the Stripe
            verification process. This ensures secure payment processing and compliance with
            financial regulations.
          </MDTypography>

          <MDTypography
            variant="body2"
            textAlign="center"
            sx={{ color: "#516340", lineHeight: 1.7 }}
          >
            If you choose "Maybe Later", you can always enable Stripe through My Account at any time.
          </MDTypography>

          {/* Complete Stripe Onboarding button */}
          <MDButton
            fullWidth
            onClick={handleStripeOnboarding}
            sx={{
              bgcolor: "#4CAF50",
              color: "#fff",
              fontWeight: 700,
              fontSize: pxToRem(15),
              borderRadius: pxToRem(10),
              py: pxToRem(13),
              mt: pxToRem(4),
              boxShadow: "0 4px 14px rgba(76,175,80,0.4)",
              "&:hover": { bgcolor: "#388E3C", boxShadow: "0 6px 18px rgba(76,175,80,0.5)" },
            }}
          >
            Complete Stripe onboarding →
          </MDButton>

          {/* Maybe later button */}
          <MDButton
            fullWidth
            variant="outlined"
            onClick={handleMaybeLater}
            sx={{
              color: "#344767",
              borderColor: "#dee2e6",
              fontWeight: 500,
              fontSize: pxToRem(15),
              borderRadius: pxToRem(10),
              py: pxToRem(13),
              bgcolor: "#fff",
              "&:hover": { borderColor: "#344767", bgcolor: "#f8f9fa" },
            }}
          >
            Maybe later
          </MDButton>
        </MDBox>
      </Modal>
    </DashboardProjectManagerLayout>
  );
}

export default observer(ProjectsPage);
